import { createContext, memo, useContext, type CSSProperties, type ReactNode } from "react";
import { Handle, NodeToolbar, Position, type Node, type NodeProps } from "@xyflow/react";
import { agentShortName } from "@baton/contracts";
import type { AgentNodeData, RepositoryNodeData } from "./model.js";
import type { Presence } from "../chat/thread.js";

export interface AgentRef { agentId: string; handle: string }

export interface AgentCreateSeed {
  position?: { x: number; y: number };
  wireFromAgentId?: string;
  repositoryId?: string | null;
}

export interface WireRef { wireId: string; sourceAgentId: string; targetAgentId: string }

/**
 * Everything a node or edge may ask the cockpit to do. The canvas never talks to the
 * API itself; the cockpit owns the calls and the reloads.
 */
export interface FlowActions {
  chat(agent: AgentRef): void;
  openWire(wire: WireRef): void;
  connect(agent: AgentRef): void;
  assign(agentId: string, repositoryId: string | null): Promise<void>;
  removeAssignment(assignmentId: string): Promise<void>;
  createWire(wire: { id: string; sourceAgentId: string; targetAgentId: string }): Promise<void>;
  removeWire(wireId: string): Promise<void>;
  createAgent(seed?: AgentCreateSeed): void;
  addRepository(): void;
  unlinkRepository(repositoryId: string, label: string): void;
  invite(): void;
  permissions: { isAdmin: boolean; isOwner: boolean };
  /** Visible repositories, for "assign to" menus. */
  repositories: Array<{ id: string; label: string }>;
  /** The viewer's own live agents, for "assign one of my agents" menus. */
  ownAgents: AgentRef[];
}

const noop = () => { /* no destination wired */ };
const noopAsync = async () => { /* no destination wired */ };

export const DEFAULT_FLOW_ACTIONS: FlowActions = {
  chat: noop,
  openWire: noop,
  connect: noop,
  assign: noopAsync,
  removeAssignment: noopAsync,
  createWire: noopAsync,
  removeWire: noopAsync,
  createAgent: noop,
  addRepository: noop,
  unlinkRepository: noop,
  invite: noop,
  permissions: { isAdmin: false, isOwner: false },
  repositories: [],
  ownAgents: [],
};

const FlowActionsContext = createContext<FlowActions>(DEFAULT_FLOW_ACTIONS);
const PresenceContext = createContext<ReadonlyMap<string, Presence>>(new Map());

export function FlowActionsProvider({ actions, children }: { actions: FlowActions; children: ReactNode }) {
  return <FlowActionsContext.Provider value={actions}>{children}</FlowActionsContext.Provider>;
}

export function PresenceProvider({ presence, children }: { presence: ReadonlyMap<string, Presence>; children: ReactNode }) {
  return <PresenceContext.Provider value={presence}>{children}</PresenceContext.Provider>;
}

export function useFlowActions(): FlowActions { return useContext(FlowActionsContext); }
export function usePresence(agentId: string): Presence | null { return useContext(PresenceContext).get(agentId) ?? null; }

function count(value: number, singular: string, plural = `${singular}s`): string {
  return `${value} ${value === 1 ? singular : plural}`;
}

function GitGlyph() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M4.2 2.4v7.2" />
    <circle cx="4.2" cy="11.4" r="2" />
    <circle cx="11.8" cy="4.4" r="2" />
    <path d="M9.8 4.4H7.4a3.2 3.2 0 0 0-3.2 3.2v2" />
  </svg>;
}

/** Role icons are routing-level ids chosen at creation; unknown ids fall back to the generic agent. */
export function AgentGlyph({ icon }: { icon?: string }) {
  switch (icon) {
    case "build":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2.5 13.5 8.6 7.4M9.2 2.8a3 3 0 0 0 3.9 3.9l-1.6 1.6-3.9-3.9 1.6-1.6ZM11.4 9.3l2.1 2.1" /></svg>;
    case "review":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M1.8 8s2.3-4.2 6.2-4.2S14.2 8 14.2 8s-2.3 4.2-6.2 4.2S1.8 8 1.8 8Z" /><circle cx="8" cy="8" r="1.9" /></svg>;
    case "merge":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="4" cy="3.5" r="1.7" /><circle cx="4" cy="12.5" r="1.7" /><circle cx="12" cy="8" r="1.7" /><path d="M4 5.2v5.6M4 5.2c0 3 6.3 1 6.3 2.8" /></svg>;
    case "test":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6 1.8h4M7 1.8v4.4L3.2 12.4A1.4 1.4 0 0 0 4.4 14.5h7.2a1.4 1.4 0 0 0 1.2-2.1L9 6.2V1.8" /></svg>;
    case "plan":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 4h8.5M5 8h8.5M5 12h8.5M2.2 4h.01M2.2 8h.01M2.2 12h.01" /></svg>;
    case "docs":
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 1.8h5.5L13 5.3v8.9H4V1.8Z" /><path d="M9.5 1.8v3.5H13M6 8.4h4M6 10.9h4" /></svg>;
    default:
      return <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <rect x="3.2" y="4.6" width="9.6" height="8" rx="2.2" />
        <path d="M8 1.6v3M6 8.2h.01M10 8.2h.01M6.4 10.6h3.2" />
      </svg>;
  }
}

function accentStyle(color: string): CSSProperties {
  return { "--node-accent": color } as CSSProperties;
}

/** A grey git block: distinct from agents on purpose. Drag its bottom handle onto an agent to assign. */
function RepositoryNode({ data, selected }: NodeProps<Node<RepositoryNodeData>>) {
  const actions = useFlowActions();
  const label = `${data.ownerHandle}/${data.name}`;
  return <article className={`flow-node flow-node-repository${selected ? " is-selected" : ""}`}>
    <Handle type="source" position={Position.Bottom} id="out" isConnectableEnd={false} className="flow-handle flow-handle-repo" />
    <NodeToolbar isVisible={selected} position={Position.Top} className="flow-node-toolbar nodrag nopan">
      {actions.ownAgents.length > 0 && <details className="flow-menu-details">
        <summary>Assign my agent</summary>
        <ul role="menu">
          {actions.ownAgents.map((agent) => <li key={agent.agentId}>
            <button type="button" role="menuitem" onClick={() => void actions.assign(agent.agentId, data.repositoryId)}>{agent.handle}</button>
          </li>)}
        </ul>
      </details>}
      {actions.permissions.isAdmin && <button type="button" onClick={() => actions.unlinkRepository(data.repositoryId, label)}>Unlink</button>}
    </NodeToolbar>
    <p className="flow-node-eyebrow"><GitGlyph />GitHub</p>
    <h3 title={label}>{label}</h3>
    <p className="muted compact">git repository</p>
    <p className="flow-node-meta">
      <span className="flow-chip">{data.defaultBranch}</span>
      {data.isPrivate && <span className="flow-chip">private</span>}
      {data.archived && <span className="flow-chip flow-chip-warn">archived</span>}
      {selected && <span className="flow-chip">{data.permission}</span>}
    </p>
  </article>;
}

function AgentNode({ data, selected }: NodeProps<Node<AgentNodeData>>) {
  const actions = useFlowActions();
  const presence = usePresence(data.agentId);
  const online = presence?.online ?? false;
  const owner = data.ownerHandle ?? "unknown member";
  const ref: AgentRef = { agentId: data.agentId, handle: data.handle };
  const unassignedRepositories = actions.repositories.filter((repository) => !data.repositoryIds.includes(repository.id));
  const canRemove = data.ownedByViewer || actions.permissions.isAdmin;
  const repositoryLabel = (repositoryId: string | null) =>
    repositoryId === null ? "whole project" : actions.repositories.find((repository) => repository.id === repositoryId)?.label ?? "repository";
  // The person and the agent, separately: "@owner" on one line, the agent's own name on the next.
  const shortName = agentShortName(data, data.ownerHandle);
  const roleLabel = data.role ?? "general";
  return <article
    className={`flow-node flow-node-agent${selected ? " is-selected" : ""}${online ? " is-online" : ""}${data.liveState ? ` is-live tone-${data.liveState}` : ""}${data.isViewerGeneral ? " is-you" : ""}`}
    style={accentStyle(data.color)}
    data-online={online ? "true" : "false"}
  >
    <Handle type="target" position={Position.Top} id="repo" isConnectableStart={false} className="flow-handle flow-handle-repo" />
    <Handle type="target" position={Position.Left} id="in" isConnectableStart={false} className="flow-handle" />
    <Handle type="source" position={Position.Right} id="out" isConnectableEnd={false} className="flow-handle" />
    <NodeToolbar isVisible={selected} position={Position.Top} className="flow-node-toolbar nodrag nopan">
      {!data.isViewerGeneral && <button type="button" onClick={() => actions.chat(ref)}>Chat</button>}
      {data.ownedByViewer && <button type="button" onClick={() => actions.connect(ref)}>Connect</button>}
      {data.ownedByViewer && <details className="flow-menu-details">
        <summary>Assign to</summary>
        <ul role="menu">
          {!data.projectWide && <li><button type="button" role="menuitem" onClick={() => void actions.assign(data.agentId, null)}>Whole project</button></li>}
          {unassignedRepositories.map((repository) => <li key={repository.id}>
            <button type="button" role="menuitem" onClick={() => void actions.assign(data.agentId, repository.id)}>{repository.label}</button>
          </li>)}
          {data.projectWide && unassignedRepositories.length === 0 && <li className="muted">Assigned everywhere</li>}
        </ul>
      </details>}
      {canRemove && <details className="flow-menu-details">
        <summary>Unassign</summary>
        <ul role="menu">
          {data.assignments.map((assignment) => <li key={assignment.assignmentId}>
            <button type="button" role="menuitem" onClick={() => void actions.removeAssignment(assignment.assignmentId)}>
              {repositoryLabel(assignment.repositoryId)}
            </button>
          </li>)}
        </ul>
      </details>}
    </NodeToolbar>
    <p className="flow-node-eyebrow">
      <AgentGlyph icon={data.icon} />agent
      <span className={`flow-node-presence${online ? " online" : ""}`} aria-label={online ? "online" : "offline"}>
        <span className={`presence-dot${online ? " online" : ""}`} aria-hidden="true" />
        {online ? `online · ${presence?.client ?? "client"}` : "offline"}
      </span>
    </p>
    <p className="flow-node-owner" title={`Owned by ${owner}`}>@{owner}</p>
    <h3 title={data.handle}>{shortName}</h3>
    <p className="flow-node-meta">
      {roleLabel !== shortName && <span className="flow-chip flow-chip-accent">{roleLabel}</span>}
      {data.ownedByViewer && <span className="flow-chip flow-chip-you">{data.isViewerGeneral ? "you" : "yours"}</span>}
      {data.projectWide && <span className="flow-chip">whole project</span>}
      {data.repositoryIds.length > 0 && <span className="flow-chip">{count(data.repositoryIds.length, "repo")}</span>}
    </p>
    <div className="flow-node-actions">
      {!data.isViewerGeneral && <button type="button" className="nodrag flow-node-action" onClick={() => actions.chat(ref)}>Chat</button>}
      {data.ownedByViewer && <button type="button" className="nodrag flow-node-action" onClick={() => actions.connect(ref)}>Connect</button>}
    </div>
  </article>;
}

export const BATON_NODE_TYPES = {
  repository: memo(RepositoryNode),
  agent: memo(AgentNode),
};
